import React from 'react';
import classNames from 'classnames';
import { compose } from 'recompose';

import appActions from 'common/support/applicationActions';
import device from 'common/support/device';
import FocusGroup from 'components/focus/FocusGroup';
import withRestoration, { WithRestorationProps, RestorationState } from 'components/withRestoration';

import { getAppScalingFactor } from 'support/scalingHelper';

import FocusManagerHOC, { WithFocusManagerProps } from 'components/hoc/FocusManagerHOC';

import textToSpeechManager from 'support/tts/TextToSpeechManager';
import styles from './HomeListRows.module.scss';

// We need to manually calculate the scaling factor (app width / 1920) since certain measurements
// are not scaled appropriately even though the element is affected by transform: scale
// getBoundingClientRect values are scaled automatically, whereas offset (ie. offsetTop/offsetLeft)
// and buffers need to be scaled manually
const scaling = getAppScalingFactor();

const ScrollDirection = {
  UP: 'up',
  DOWN: 'down',
} as const;

type ScrollDirectionType = (typeof ScrollDirection)[keyof typeof ScrollDirection];

interface RowData {
  key?: string;
  id?: string;
  title?: string;
}

interface HomeListRowsOwnProps {
  focusId?: string | number | null;
  className?: string | null;
  renderItem: (row: RowData, index: number) => React.ReactNode;
  data: RowData[];
  onListRowsFocusIndexChanged?: (index: number) => void;
  shouldSnapRowToTop?: (index: number) => boolean;
  buffer: number;
  style?: React.CSSProperties;
  ttsEndOfNavigation?: string | null;
  restorationId?: string;
}

type HomeListRowsProps = HomeListRowsOwnProps & WithRestorationProps & WithFocusManagerProps;

interface HomeListRowsState {
  isAnimating: boolean;
  top: number;
  startIndex: number;
  endIndex: number;
  isFocusOnRows: boolean;
}

class HomeListRows extends React.PureComponent<HomeListRowsProps, HomeListRowsState> {
  static defaultProps: Partial<HomeListRowsOwnProps> = {
    className: null,
    onListRowsFocusIndexChanged: () => {},
    shouldSnapRowToTop: () => false,
    style: {},
    ttsEndOfNavigation: null,
  };

  listRowsViewport = React.createRef<HTMLDivElement>();

  focusGroupRef = React.createRef<FocusGroup>();

  focusedIndex = 0;

  rowRefs: React.RefObject<HTMLDivElement>[] = [];

  rowHeights: (number | undefined)[] = [];

  timeout: ReturnType<typeof setTimeout> | null = null;

  focusId: string | number | null = null;

  state: HomeListRowsState = {
    isAnimating: false,
    top: 0,
    startIndex: 0,
    endIndex: 0,
    isFocusOnRows: false,
  };

  constructor(props: HomeListRowsProps) {
    super(props);
    const { restorationState, focusManager, data } = props;
    this.focusId = props.focusId || focusManager.getNewUniqueId();
    this.state.endIndex = props.buffer;
    if (restorationState && data) {
      const {
        endIndex: oldEndIndex,
        startIndex: oldStartIndex,
      } = restorationState as HomeListRowsState;
      let newEndIndex = oldEndIndex;
      let newStartIndex = oldStartIndex;
      if (oldEndIndex > data.length - 1) {
        const amountToShift = oldEndIndex - data.length - 1;
        newEndIndex = data.length - 1;
        newStartIndex = Math.max(0, oldStartIndex - amountToShift);
      }
      this.state = {
        ...(restorationState as HomeListRowsState),
        startIndex: newStartIndex,
        endIndex: newEndIndex,
      };
    }
  }

  componentDidMount() {
    const { data, focusManager } = this.props;
    focusManager.addFocusChangedListener(this.onFocusChanged);
    if (data && data.length) {
      this.rowHeights = this.rowRefs.map(
        item => {
          return item.current?.getBoundingClientRect()?.height;
        },
      );
    }
  }

  componentDidUpdate(prevProps: HomeListRowsProps, prevState: HomeListRowsState) {
    const { data, setRestorationState } = this.props;

    if (data && data.length) {
      for (let i = 0; i < this.rowRefs.length; i += 1) {
        if (this.rowRefs[i] && this.rowRefs[i].current) {
          const rowRef = this.rowRefs[i].current;
          this.rowHeights[i] = rowRef?.getBoundingClientRect()?.height;
        }
      }
    }

    if (prevState !== this.state && this.state) {
      setRestorationState(this.state as RestorationState);
    }
  }

  componentWillUnmount() {
    const { focusManager } = this.props;
    focusManager.removeFocusChangedListener(this.onFocusChanged);
  }

  onFocusChanged = () => {
    const { focusManager } = this.props;
    const { isFocusOnRows } = this.state;
    if (!window || !focusManager.isFocusAncestor(this.focusId)) return;

    const {
      top: viewportTop,
      bottom: viewportBottom,
    } = this.listRowsViewport.current?.getBoundingClientRect() ?? {};

    const lastFocusedIndex = this.focusedIndex;
    const viewportHeight = (viewportBottom as number) - (viewportTop as number);
    const { focusedIndex, state, rowRefs, rowHeights } = this;
    const refIndex = focusedIndex + state.startIndex;
    const rowRef = rowRefs[refIndex];

    const rowOffsetTop = rowRef.current
      ? rowRef.current.offsetTop * scaling
      : 0;

    if (isFocusOnRows) {
      const { shouldSnapRowToTop } = this.props;
      const snapRowToTop = shouldSnapRowToTop!(refIndex);
      const newTop = snapRowToTop
        ? -rowOffsetTop
        : -rowOffsetTop + viewportHeight - (rowHeights[refIndex] as number);
      this.setState(
        {
          isAnimating: true,
          top: newTop,
        },
        () => {
          if (!this.timeout) {
            this.timeout = setTimeout(() => {
              this.timeout = null;
              snapRowToTop
                ? this.handleSnapping(lastFocusedIndex)
                : this.handleClipping(ScrollDirection.DOWN, lastFocusedIndex);
            }, 300);
          }
        },
      );
    } else if (!isFocusOnRows) {
      this.setState({
        isAnimating: true,
        isFocusOnRows: true,
      });
    }
  };

  updateIndices = (newStartIndex: number, newEndIndex: number) => {
    const { data } = this.props;
    const { startIndex, endIndex, top } = this.state;

    const checkedStartIndex = Math.max(0, newStartIndex);
    const checkedEndIndex = Math.min(data.length - 1, newEndIndex);

    if (
      (checkedStartIndex !== startIndex || checkedEndIndex !== endIndex) &&
      this.rowHeights.length &&
      data.length >= checkedEndIndex
    ) {
      const shouldClip = checkedStartIndex !== startIndex;
      this.setState(
        {
          isAnimating: false,
          startIndex: checkedStartIndex,
          endIndex: checkedEndIndex,
        },
        () => {
          const clipIndex =
            checkedStartIndex > startIndex ? startIndex : checkedStartIndex;
          const clipOffset = shouldClip ? this.rowHeights[clipIndex as number] : 0;

          this.setState({
            top:
              checkedStartIndex > startIndex
                ? top + (clipOffset as number)
                : top - (clipOffset as number),
          });
          if (this.focusGroupRef && this.focusGroupRef.current) {
            this.focusGroupRef.current.resetFocusIndex();
          }
        },
      );
    }
  };

  handleSnapping = (lastFocusedIndex: number) => {
    const { buffer } = this.props;
    const { startIndex } = this.state;

    // Need to add these indexes together since the FocusGroup is only
    // receiving a subset of items
    const focusedIndex = lastFocusedIndex + startIndex;

    const newStartIndex = focusedIndex - 1;
    const newEndIndex = focusedIndex + buffer;

    this.updateIndices(newStartIndex, newEndIndex);
  };

  handleClipping = (scrollDirection: ScrollDirectionType, lastFocusedIndex: number) => {
    const { buffer } = this.props;
    const { startIndex } = this.state;

    // Need to add these indexes together since the FocusGroup is only
    // receiving a subset of items
    const focusedIndex = lastFocusedIndex + startIndex;

    let newStartIndex;
    let newEndIndex;
    if (scrollDirection === ScrollDirection.UP) {
      newStartIndex = focusedIndex - 1;
      newEndIndex = focusedIndex + buffer;
    } else {
      newStartIndex = focusedIndex - buffer;
      newEndIndex = focusedIndex + 1;
    }

    this.updateIndices(newStartIndex, newEndIndex);
  };

  // Binary search for finding next focsable element
  getNextFocusableElement = (sourceElRect: DOMRect, focusableChildren: Element[]): Element => {
    // switching to fixed scrolling simplifies this logic: always navigate to card in the first position
    const targetEl = focusableChildren[0];

    const targetElLeft = targetEl.getBoundingClientRect().left;
    if (targetElLeft < 0) {
      return focusableChildren[1];
    }
    return focusableChildren[0];

    // const sourceElMiddle = (sourceElRect.left + sourceElRect.right) / 2;

    // const medianIndex = Math.floor(focusableChildren.length / 2);
    // // const targetEl = focusableChildren[medianIndex];

    // const targetElMiddleValue =
    //   (targetEl.getBoundingClientRect().left +
    //     targetEl.getBoundingClientRect().right) /
    //   2;

    // const targetElWidth = targetEl.getBoundingClientRect().width;

    // const distanceDifference = sourceElMiddle - targetElMiddleValue;
    // const absoluteDifference = Math.abs(distanceDifference);
    // if (absoluteDifference <= targetElWidth) {
    //   // We are pretty close so lets just check where we are
    //   const nextIndexToCheck =
    //     distanceDifference >= 0 ? medianIndex + 1 : medianIndex - 1;

    //   // Ensure our index is always in bounds
    //   if (
    //     nextIndexToCheck >= 0 &&
    //     nextIndexToCheck < focusableChildren.length
    //   ) {
    //     const adjacentEl = focusableChildren[nextIndexToCheck];
    //     const adjacentElMiddle =
    //       (adjacentEl.getBoundingClientRect().left +
    //         adjacentEl.getBoundingClientRect().right) /
    //       2;

    //     return absoluteDifference < Math.abs(sourceElMiddle - adjacentElMiddle)
    //       ? targetEl
    //       : adjacentEl;
    //   }
    // }

    // let currClosestEl;
    // if (!prevClosestEl) {
    //   currClosestEl = targetEl;
    // } else {
    //   const prevElMiddle =
    //     (prevClosestEl.getBoundingClientRect().left +
    //       prevClosestEl.getBoundingClientRect().right) /
    //     2;

    //   const prevClosestElDistance = Math.abs(sourceElMiddle - prevElMiddle);

    //   currClosestEl =
    //     absoluteDifference < prevClosestElDistance ? targetEl : prevClosestEl;
    // }

    // const nextArray =
    //   targetElMiddleValue > sourceElMiddle
    //     ? focusableChildren.slice(0, medianIndex)
    //     : focusableChildren.slice(medianIndex);

    // if (nextArray.length === 0 || focusableChildren.length === 1) {
    //   return currClosestEl;
    // }

    // return this.getNextFocusableElement(sourceElRect, nextArray, currClosestEl);
  };

  onActionOverride = (action: string): boolean => {
    const { focusId, focusManager } = this.props;
    if (action === appActions.moveDown || action === appActions.moveUp) {
      // Let's first get our focusable children within this component (veritcally available)
      const currentFocusableElementRect = focusManager
        .getFocusableElement(focusManager.currentFocusId)
        .getBoundingClientRect();
      const currentRowIndex = this.focusGroupRef.current?.focusIndex;
      const rowsAvailable = focusManager.getFocusableChildren(focusId);
      const lastIndex = rowsAvailable.length - 1;
      const nextIndex =
        action === appActions.moveDown
          ? (currentRowIndex as number) + 1
          : (currentRowIndex as number) - 1;
      let nextRow: Element | undefined;

      if (nextIndex >= 0 && nextIndex <= lastIndex) {
        nextRow = rowsAvailable[nextIndex];

        const nextFocusableElement = this.getNextFocusableElement(
          currentFocusableElementRect,
          focusManager.getFocusableChildren(nextRow?.getAttribute('data-fcsid')),
        );

        const {
          top: viewportTop,
          bottom: viewportBottom,
        } = this.listRowsViewport.current?.getBoundingClientRect() ?? {};

        if (nextRow) {
          const {
            top: newFocusTop,
            bottom: newFocusBottom,
          } = nextRow.getBoundingClientRect();

          if (
            this.timeout &&
            (newFocusTop < (viewportTop as number) || newFocusBottom > (viewportBottom as number))
          ) {
            return true;
          }
        }

        focusManager.setCurrentFocus(
          nextFocusableElement.getAttribute('data-fcsid'),
        );
        return true;
      }
    }
    return false;
  };

  onEndOfNavigation = () => {
    const { data } = this.props;
    const { startIndex } = this.state;
    const index = this.focusedIndex + startIndex;
    if (index === 0 || index === data.length - 1) {
      const { ttsEndOfNavigation } = this.props;
      textToSpeechManager.add(ttsEndOfNavigation);
      textToSpeechManager.speak();
    }
    if (index === 0) {
      this.setState({ isAnimating: true, isFocusOnRows: false });
    }
  };

  onFocusIndexChanged = (index: string | number) => {
    const { startIndex, isFocusOnRows } = this.state;
    const { onListRowsFocusIndexChanged } = this.props;
    this.focusedIndex = index as number;

    if (!isFocusOnRows) {
      onListRowsFocusIndexChanged?.(-1);
    } else {
      onListRowsFocusIndexChanged?.((index as number) + startIndex);
    }
  };

  resetIndex = async (): Promise<void> => {
    return new Promise(res => {
      this.focusedIndex = 0;
      this.setState(
        {
          isAnimating: false,
          top: 0,
          startIndex: 0,
          endIndex: 0,
          isFocusOnRows: false,
        },
        () => {
          this.onFocusIndexChanged(0);
          res();
        },
      );
    });
  };

  getFocusedIndex = () => this.focusedIndex;

  render() {
    const { className, renderItem, data, style } = this.props;
    const {
      top,
      isAnimating,
      startIndex,
      endIndex,
      isFocusOnRows,
    } = this.state;

    return (
      <div className={className ?? undefined} ref={this.listRowsViewport} style={style}>
        <FocusGroup
          className={classNames(
            {
              [styles.animate]: isAnimating && !device.isSlowDevice,
            },
            'text-nowrap',
            'd-block',
            'position-relative',
          )}
          ref={this.focusGroupRef}
          focusId={this.focusId ? this.focusId.toString() : undefined}
          onActionOverride={this.onActionOverride}
          onFocusIndexChanged={this.onFocusIndexChanged}
          onFocusDelegated={this.onFocusIndexChanged}
          style={{
            paddingTop: `80px`,
            transform: isFocusOnRows
              ? `translateY(${top / scaling}px)`
              : 'translateY(420px)',
            WebkitTransform: isFocusOnRows
              ? `translateY(${top / scaling}px)`
              : 'translateY(420px)',
          }}
          onEndOfNavigation={this.onEndOfNavigation}
        >
          {data &&
            !!data.length &&
            data.slice(startIndex, endIndex + 1).map((row, index) => {
              const totalIndex = startIndex + index;
              if (!this.rowRefs[totalIndex]) {
                this.rowRefs[totalIndex] = React.createRef<HTMLDivElement>();
              }
              return (
                <div
                  key={row.key || row.id || row.title}
                  ref={this.rowRefs[totalIndex]}
                >
                  {renderItem(row, index + startIndex)}
                </div>
              );
            })}
        </FocusGroup>
      </div>
    );
  }
}

export default compose<HomeListRowsProps, HomeListRowsOwnProps>(
  FocusManagerHOC,
  withRestoration<HomeListRowsOwnProps & Record<string, unknown>>(props => props.restorationId)
)(HomeListRows);
